import { watch, ref } from 'vue';
import { MdTypography } from '@antiqua-dls/vue-components';
import { useI18n } from 'vue-i18n';
import { ERROR_TYPES, VALIDATION_EVENT_TYPE } from 'src/utils/constants/common';

import { useAiTextEditorValidations } from '../../../../../../composables/useAiTextEditor/validator.js';

export default {
  name: 'ErrorContainer',
  components: {
    MdTypography
  },
  props: {
    validationData: {
      type: Object,
      required: true
    },
    aiSuggestedErrors: {
      type: Object,
      required: true
    },
    forcedErrors: {
      type: Array,
      default: () => []
    },
    isAiEnabled: {
      type: Boolean,
      default: false
    },
    foundryId: {
      type: String,
      default: ''
    },
    moduleName: {
      type: String,
      default: ''
    }
  },
  emits: ['errorStatus', 'onValidationDone', 'bannedWordUpdate'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const emptyDescriptionErrorMessage = t('pages.aiTextEditor.errors.empty_description');

    // composables
    const {
      checkMinMaxLength,
      checkForPriceInText,
      checkForContactInfoInText,
      checkForExtraSpaces,
      checkForBannedWords,
      checkForFontFormats,
      checkForFreeFonts,
      analyzeLanguageContent
    } = useAiTextEditorValidations();

    // states
    const errorsToRender = ref({
      forcedErrors: [],
      aiSuggestedErrors: [],
      validationErrors: []
    });
    const errorOnEvent = ref({
      changeValidationErrors: [],
      blurValidationErrors: []
    });
    const prevFilterWords = ref({
      warning: [],
      banned: []
    });
    const emitValidation = ref(false);

    // methods
    const runOnChangeValidations = () => {
      const errors = [];
      const result = checkMinMaxLength(
        props.validationData.textLength,
        props.validationData.minLimit,
        props.validationData.maxLimit,
        props.isAiEnabled,
        props.moduleName
      );
      const { errorMessage, errorCode } = result || {};
      if (errorMessage) {
        errors.push({
          message: errorMessage,
          type: ERROR_TYPES.error,
          errorCode
        });
      }
      errorOnEvent.value.changeValidationErrors = errors;
    };

    const runOnBlurValidations = () => {
      const newErrors = [];

      const languageAnalysisError = analyzeLanguageContent(props.validationData.plainText);
      if (languageAnalysisError) {
        newErrors.push({
          message: languageAnalysisError,
          type: ERROR_TYPES.error
        });
      }

      const extraSpacesError = checkForExtraSpaces(props.validationData.text);
      if (extraSpacesError) {
        newErrors.push({
          message: extraSpacesError,
          type: ERROR_TYPES.error
        });
      }

      const priceError = checkForPriceInText(props.validationData.text);
      if (priceError) {
        newErrors.push({
          message: priceError,
          type: ERROR_TYPES.error
        });
      }

      const contactInfoErrors = checkForContactInfoInText(props.validationData.text);
      if (contactInfoErrors?.length > 0) {
        for (const error of contactInfoErrors) {
          newErrors.push({
            message: error,
            type: ERROR_TYPES.error
          });
        }
      }

      const fontFormatError = checkForFontFormats(props.validationData.text);
      if (fontFormatError) {
        newErrors.push({
          message: fontFormatError,
          type: ERROR_TYPES.error
        });
      }

      const freeFontError = checkForFreeFonts(props.validationData.text);
      if (freeFontError) {
        newErrors.push({
          message: freeFontError,
          type: ERROR_TYPES.error
        });
      }

      const bannedWordInfo = checkForBannedWords(props.validationData.text, props.foundryId, prevFilterWords.value);
      prevFilterWords.value.warning = bannedWordInfo.warningWordMatches;
      prevFilterWords.value.banned = bannedWordInfo.banned;

      emit('bannedWordUpdate', {
        status: bannedWordInfo.bannedWordExist,
        text: bannedWordInfo.description
      });
      if (bannedWordInfo.bannedWordExist) {
        const bannedWordErrorMessage = t('pages.profile.foundry_profile.banned_word_error_message');
        newErrors.push({
          message: bannedWordErrorMessage,
          type: ERROR_TYPES.error
        });
      }

      if (bannedWordInfo.warningWordExist) {
        const warningWordsList = bannedWordInfo.warningWordMatches.join('", "');
        const warningWordErrorMessage = t('pages.profile.foundry_profile.warning_word_error_message', {
          words: warningWordsList
        });

        newErrors.push({
          message: warningWordErrorMessage,
          type: ERROR_TYPES.warning
        });
      }

      errorOnEvent.value.blurValidationErrors = newErrors;
    };

    const runAiErrorValidations = () => {
      const aiErrorsObj = props.aiSuggestedErrors;
      const aiErrors = [];
      if (aiErrorsObj && Array.isArray(aiErrorsObj)) {
        for (const error of aiErrorsObj) {
          aiErrors.push({
            message: error.errorMessage,
            type: ERROR_TYPES.error
          });
        }
      }
      if (aiErrors.length) {
        emitValidation.value = true;
      }
      errorsToRender.value.aiSuggestedErrors = aiErrors;
    };

    // Update overall error status
    const updateErrorStatus = () => {
      const allErrors = [
        ...errorsToRender.value.validationErrors,
        ...errorsToRender.value.aiSuggestedErrors,
        ...errorsToRender.value.forcedErrors
      ];

      let hasError = false;
      let hasWarning = false;
      let disableAIAction = false;
      let onlyErrorIsTextTooLong = false;
      let errorCount = 0;

      for (const err of allErrors) {
        if (err.type === ERROR_TYPES.error) {
          hasError = true;
          errorCount++;
        } else if (err.type === ERROR_TYPES.warning) {
          hasWarning = true;
        }
      }

      if (
        errorCount === 1 &&
        props.validationData.textLength > props.validationData.maxLimit &&
        allErrors.find(err => err.type === ERROR_TYPES.error && err.errorCode === 'TEXT_TOO_LONG')
      ) {
        onlyErrorIsTextTooLong = true;
      }

      if (!onlyErrorIsTextTooLong && hasError) {
        disableAIAction = true;
      }

      emit('errorStatus', {
        hasError,
        hasWarning,
        disableAIAction
      });
      if (props?.validationData?.eventType === 'blur' || emitValidation.value) {
        emitValidation.value = false;
        emit('onValidationDone', hasError);
      }
    };

    // watcher for validationData changes
    watch(
      [() => props.validationData, () => props.aiSuggestedErrors, () => props.forcedErrors],
      ([newValidation, newAiErrors, newForcedErrors]) => {
        if (props.validationData.textLength > 0) {
          if (props.validationData && props.validationData.eventType === VALIDATION_EVENT_TYPE.CHANGE) {
            runOnChangeValidations();
          }
          if (props.validationData && props.validationData.eventType === VALIDATION_EVENT_TYPE.BLUR) {
            // blur should run change validation also, to handle a case where user already has something,
            // and user does not change but focus in-out to see if there is any error
            runOnChangeValidations();
            runOnBlurValidations();
          }

          const combinedErrors = [
            ...errorOnEvent.value.changeValidationErrors,
            ...errorOnEvent.value.blurValidationErrors
          ];

          errorsToRender.value.validationErrors = [...new Set(combinedErrors)];
        } else {
          errorOnEvent.value = {
            changeValidationErrors: [],
            blurValidationErrors: []
          };
          errorsToRender.value.validationErrors = [
            {
              message: emptyDescriptionErrorMessage,
              type: ERROR_TYPES.error
            }
          ];
        }

        // Handle AI suggested errors
        runAiErrorValidations();

        // Handle forced errors
        const forcedErrors = [];
        // Replace with actual forced error logic
        if (newForcedErrors && newForcedErrors.length > 0) {
          forcedErrors.push(...newForcedErrors);
        }
        errorsToRender.value.forcedErrors = forcedErrors;
        updateErrorStatus();
      },
      { deep: true }
    );

    return {
      errorsToRender,
      errorOnEvent,
      errorTypes: ERROR_TYPES
    };
  }
};
